import { useMemo, useState, useEffect } from 'react'
import { Modal, Input, Checkbox, Empty, Spin, Button } from 'antd'
import { SearchOutlined, CloseCircleFilled, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import type { BroadcastUserItem } from '@/service/broadcast/types/getBroadcastUserList'
import Overflow from 'rc-overflow'
import styles from './addUser.module.scss'

interface AddUserProps {
    value?: string[]
    onChange?: (value: string[]) => void
    userList: BroadcastUserItem[]
    loading?: boolean
    placeholder?: string
}

const AddUser = ({
    value = [],
    onChange,
    userList,
    loading,
    placeholder = 'Select users',
}: AddUserProps) => {
    const [open, setOpen] = useState(false)
    const [keyword, setKeyword] = useState('')
    const [selected, setSelected] = useState<string[]>(value)

    useEffect(() => {
        if (open) setSelected(value)
    }, [open, value])

    const filteredList = useMemo(() => {
        const kw = keyword.trim().toLowerCase()
        if (!kw) return userList
        return userList.filter(u =>
            (u.nickname || '').toLowerCase().includes(kw) ||
            u.user_id.includes(kw),
        )
    }, [keyword, userList])

    const userMap = useMemo(() => {
        const m = new Map<string, BroadcastUserItem>()
        userList.forEach(u => m.set(u.user_id, u))
        return m
    }, [userList])

    const removeOne = (id: string, e: React.MouseEvent) => {
        e.stopPropagation()
        onChange?.(value.filter(v => v !== id))
    }

    const filteredIds = useMemo(
        () => filteredList.map(u => u.user_id),
        [filteredList],
    )
    const allFilteredChecked =
        filteredIds.length > 0 && filteredIds.every(id => selected.includes(id))
    const someFilteredChecked =
        filteredIds.some(id => selected.includes(id)) && !allFilteredChecked

    const handleToggleAll = (checked: boolean) => {
        if (checked) {
            setSelected(Array.from(new Set([...selected, ...filteredIds])))
        } else {
            const set = new Set(filteredIds)
            setSelected(selected.filter(id => !set.has(id)))
        }
    }

    const handleToggle = (id: string) => {
        setSelected(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
        )
    }

    const handleOk = () => {
        onChange?.(selected)
        setOpen(false)
        setKeyword('')
    }

    const handleCancel = () => {
        setOpen(false)
        setKeyword('')
    }

    return (
        <>
            <div
                className={`${styles.trigger} ${value.length ? '' : styles.placeholder}`}
                onClick={() => setOpen(true)}
            >
                {value.length === 0 ? (
                    <span className={styles.triggerText}>{placeholder}</span>
                ) : (
                    <Overflow
                        className={styles.tagsWrap}
                        data={value}
                        maxCount="responsive"
                        renderItem={(id: string) => {
                            const label = userMap.get(id)?.nickname || id
                            return (
                                <span className={styles.tag}>
                                    <span className={styles.tagLabel}>{label}</span>
                                    <CloseOutlined
                                        className={styles.tagClose}
                                        onClick={(e) => removeOne(id, e)}
                                    />
                                </span>
                            )
                        }}
                        renderRest={(items) => (
                            <span className={styles.moreTag}>+{items.length}</span>
                        )}
                    />
                )}
                {value.length > 0 && (
                    <CloseCircleFilled
                        className={styles.triggerClear}
                        onClick={(e) => {
                            e.stopPropagation()
                            onChange?.([])
                        }}
                    />
                )}
            </div>

            <Modal
                open={open}
                title="Select Users"
                onCancel={handleCancel}
                width={700}
                destroyOnHidden
                className="add-user-modal"
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button
                            onClick={handleCancel}
                            style={{
                                color: '#5793FA',
                                background: '#121417',
                                border: '1px solid #5793FA',
                                borderRadius: 8,
                                height: 32,
                                width: 120,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleOk}
                            disabled={selected.length === 0}
                            style={{
                                color: selected.length === 0 ? '#515766' : '#25D1F7',
                                background: selected.length === 0
                                    ? '#1A1C22'
                                    : 'rgba(37, 209, 247, 0.08)',
                                border: `1px solid ${selected.length === 0 ? '#2A2D36' : '#25D1F7'}`,
                                borderRadius: 8,
                                fontWeight: 600,
                                height: 32,
                                width: 120,
                                cursor: selected.length === 0 ? 'not-allowed' : 'pointer',
                            }}
                        >
                            OK ({selected.length})
                        </Button>
                    </div>
                }
            >
                <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search by nickname"
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    allowClear
                />

                <div className={styles.actionBar}>
                    <Checkbox
                        checked={allFilteredChecked}
                        indeterminate={someFilteredChecked}
                        onChange={e => handleToggleAll(e.target.checked)}
                        disabled={filteredIds.length === 0}
                    >
                        Select All ({filteredIds.length})
                    </Checkbox>
                    <span className={styles.counter}>
                        Selected: {selected.length}
                    </span>
                </div>

                <div className={styles.list}>
                    {loading ? (
                        <div className={styles.center}><Spin /></div>
                    ) : filteredList.length === 0 ? (
                        <div className={styles.center}>
                            <Empty description="No users" />
                        </div>
                    ) : (
                        filteredList.map(u => {
                            const isSelected = selected.includes(u.user_id)
                            return (
                                <div
                                    key={u.user_id}
                                    className={`${styles.row} ${isSelected ? styles.rowSelected : ''}`}
                                    onClick={() => handleToggle(u.user_id)}
                                >
                                    <Checkbox
                                        checked={isSelected}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => handleToggle(u.user_id)}
                                    />
                                    <div className={styles.info}>
                                        <div className={styles.nickname}>
                                            {u.nickname || u.user_id}
                                        </div>
                                        <div className={styles.userId}>
                                            {u.user_id}
                                        </div>
                                    </div>
                                    {isSelected && <CheckOutlined className={styles.check} />}
                                </div>
                            )
                        })
                    )}
                </div>
            </Modal>
        </>
    )
}

export default AddUser